import { FC, ReactNode, useEffect, useState } from "react"
import classNames from "classnames"
import {
  Container,
  Typography,
  Button as MarmaladeButton,
  AnimatedLoadingIcon,
  CalendarOffIcon
} from "@eventbrite/marmalade"

// styles
import * as MyEventsStyles from "./MyEvents.module.scss"
import { EventbriteEvent } from "../../../utils/auth/enrich_user_data"
import {
  customRendererToRemovePTag,
  renderRichText
} from "../../../utils/contentful/render_rich_text"
import { getBaseUrl } from "../../../utils/general_utils"
import { handleMobileAppClick } from "../../../utils/personalization/shared"
import { formatUTCDate } from "../Attendee/MyTickets/MyTickets"
import placeholderSvgImg from "../../../../static/images/event_placeholder_image.svg"
import { useSelector } from "react-redux"
import { RootState } from "../../../store/store"

type TrimmedSingleEvent = {
  title: string
  imageUrl: string
  displayDate: string
  eventPageUrl: string
}

type ContentfulContent =
  | {
      noEventsFoundActionComplex: {
        eventActionComplexHeader: ReactNode
        eventActionComplexPrimaryCta: ReactNode
        eventActionComplexSecondaryCta: ReactNode
        eventActionComplexTertiaryCta: ReactNode
      }
      eventsFoundActionComplex: {
        eventActionComplexHeader: ReactNode
        eventActionComplexPrimaryCta: ReactNode
        eventActionComplexSecondaryCta: ReactNode
        eventActionComplexTertiaryCta: ReactNode
      }
      searchingForEvents: ReactNode
      messageNoUpcomingEvents: ReactNode
    }
  | Record<string, never>

export type MyEventsProps = {
  noEventsFoundActionComplex: Queries.HomepageDataQuery["noEventsFoundActionComplex"]
  eventsFoundActionComplex: Queries.HomepageDataQuery["eventsFoundActionComplex"]
  searchingForEvents: Queries.HomepageDataQuery["searchingForEvents"]
  messageNoUpcomingEvents: Queries.HomepageDataQuery["messageNoUpcomingEvents"]
  columnLayout?: boolean
  events?: EventbriteEvent[]
}

export const MyEvents: FC<MyEventsProps> = ({
  columnLayout,
  events,
  noEventsFoundActionComplex,
  eventsFoundActionComplex,
  searchingForEvents,
  messageNoUpcomingEvents
}) => {
  const [trimmedEvents, setTrimmedEvents] = useState<TrimmedSingleEvent[]>([])
  const activeEvent = trimmedEvents.length > 0 ? trimmedEvents[0] : null
  const [contentfulContent, setContentfulContent] = useState<ContentfulContent>(
    {}
  )
  const hasAttemptedLogin = useSelector(
    (state: RootState) => state.metaData.hasAttemptedLogin
  )

  useEffect(() => {
    const x: ContentfulContent = Object.assign(contentfulContent)

    if (noEventsFoundActionComplex) {
      x.noEventsFoundActionComplex = {
        eventActionComplexHeader:
          noEventsFoundActionComplex?.eventActionComplexHeader,
        eventActionComplexPrimaryCta: renderRichText(
          noEventsFoundActionComplex?.eventActionComplexPrimaryCta
            ?.microcopyValue?.raw ?? "",
          customRendererToRemovePTag
        ),
        eventActionComplexSecondaryCta: renderRichText(
          noEventsFoundActionComplex?.eventActionComplexSecondaryCta
            ?.microcopyValue?.raw ?? "",
          customRendererToRemovePTag
        ),
        eventActionComplexTertiaryCta: renderRichText(
          noEventsFoundActionComplex?.eventActionComplexTertiaryCta
            ?.microcopyValue?.raw ?? "",
          customRendererToRemovePTag
        )
      }
    }

    if (eventsFoundActionComplex) {
      x.eventsFoundActionComplex = {
        eventActionComplexHeader:
          eventsFoundActionComplex?.eventActionComplexHeader,
        eventActionComplexPrimaryCta: renderRichText(
          eventsFoundActionComplex?.eventActionComplexPrimaryCta?.microcopyValue
            ?.raw ?? "",
          customRendererToRemovePTag
        ),
        eventActionComplexSecondaryCta: renderRichText(
          eventsFoundActionComplex?.eventActionComplexSecondaryCta
            ?.microcopyValue?.raw ?? "",
          customRendererToRemovePTag
        ),
        eventActionComplexTertiaryCta: renderRichText(
          eventsFoundActionComplex?.eventActionComplexTertiaryCta
            ?.microcopyValue?.raw ?? "",
          customRendererToRemovePTag
        )
      }
    }

    if (messageNoUpcomingEvents) {
      x.messageNoUpcomingEvents = renderRichText(
        messageNoUpcomingEvents.microcopyValue?.raw ?? "",
        customRendererToRemovePTag
      )
    }

    if (searchingForEvents) {
      x.searchingForEvents = renderRichText(
        searchingForEvents?.eventActionComplexLoadingMessage?.microcopyValue
          ?.raw ?? "",
        customRendererToRemovePTag
      )
    }

    setContentfulContent(x)
  }, [
    noEventsFoundActionComplex,
    eventsFoundActionComplex,
    searchingForEvents,
    events
  ])

  useEffect(() => {
    if (events && events?.length > 0) {
      const filteredEvents: TrimmedSingleEvent[] = events.map((x) => {
        const singleEvent = {} as TrimmedSingleEvent

        if (x.logo?.url) {
          singleEvent.imageUrl = x.logo.url
        }
        if (x.name?.text) {
          singleEvent.title = x.name.text
        }
        if (x.start?.utc) {
          singleEvent.displayDate = formatUTCDate(x.start.utc)
        }
        if (x.id) {
          singleEvent.eventPageUrl = `${
            getBaseUrl() ? getBaseUrl() : process.env.GATSBY_PRODUCT_SITE_URL
          }/myevent?eid=${x.id}`
        }

        return singleEvent
      })

      setTrimmedEvents(filteredEvents)
    } else {
      setTrimmedEvents([])
    }
  }, [events])

  if (!hasAttemptedLogin) {
    return (
      <Container
        className={classNames(MyEventsStyles.myeventsOuterContainer, {
          [`${MyEventsStyles.myeventsColumnContainer}`]: columnLayout
            ? true
            : false
        })}
      >
        <Typography
          className={MyEventsStyles.myeventsTitle}
          variant="heading-sm"
          color="neutral-1000"
        >
          {contentfulContent?.noEventsFoundActionComplex
            ?.eventActionComplexHeader
            ? contentfulContent?.noEventsFoundActionComplex
                ?.eventActionComplexHeader
            : "My events"}
        </Typography>
        <div style={{ textAlign: "center", padding: "2rem 0" }}>
          <AnimatedLoadingIcon
            className={MyEventsStyles.loaderSvg}
            color="#3659E3"
          />
          <Typography
            variant="body-sm"
            style={{ marginTop: "1rem", color: "#C1BDC7" }}
          >
            {contentfulContent?.searchingForEvents
              ? contentfulContent.searchingForEvents
              : "Searching for your events..."}
          </Typography>
        </div>
      </Container>
    )
  }

  if (!events || events?.length <= 0) {
    return (
      <Container
        className={classNames(MyEventsStyles.myeventsOuterContainer, {
          [`${MyEventsStyles.myeventsColumnContainer}`]: columnLayout
            ? true
            : false
        })}
      >
        <div className={MyEventsStyles.myeventsTitleWrapper}>
          <Typography
            className={MyEventsStyles.myeventsTitle}
            variant="heading-sm"
            color="neutral-1000"
          >
            {contentfulContent?.noEventsFoundActionComplex
              ?.eventActionComplexHeader
              ? contentfulContent?.noEventsFoundActionComplex
                  ?.eventActionComplexHeader
              : "My events"}
          </Typography>
        </div>
        <div className={MyEventsStyles.noEventsTopContainer}>
          <CalendarOffIcon size="medium" color="#3A3247" />
          <Typography variant="body-lg">
            {contentfulContent?.messageNoUpcomingEvents
              ? contentfulContent.messageNoUpcomingEvents
              : "No upcoming events"}
          </Typography>
          <EventsBtn 
            className={MyEventsStyles.noEventsManageMyEventsBtn}
            heapId="button.organizer-component.manage-my-events"
          >
            {contentfulContent?.eventsFoundActionComplex
              ?.eventActionComplexTertiaryCta
              ? contentfulContent?.eventsFoundActionComplex
                  ?.eventActionComplexTertiaryCta
              : "Manage my events"}
          </EventsBtn>
        </div>

        <div
          className={classNames(MyEventsStyles.noEventsBottomContainer, {
            [`${MyEventsStyles.noEventsFindMyEventsColumnWrapper}`]:
              columnLayout ? true : false
          })}
        >
          <div
            className={MyEventsStyles.buttonDiv}
            onClick={(e) =>
              handleMobileAppClick(
                `${
                  getBaseUrl()
                    ? getBaseUrl()
                    : process.env.GATSBY_PRODUCT_SITE_URL
                }/organizations/home`,
                e
              )
            }
            onTouchStart={(e) => e.preventDefault()}
            onTouchEnd={(e) => {
              e.preventDefault()
              handleMobileAppClick(
                `${
                  getBaseUrl()
                    ? getBaseUrl()
                    : process.env.GATSBY_PRODUCT_SITE_URL
                }/organizations/home`,
                e
              )
            }}
            data-heap-id="button.organizer-component.create-an-event"
            style={{ cursor: "pointer" }}
          >
            <MarmaladeButton variant="primary">
              +{" "}
              {contentfulContent?.noEventsFoundActionComplex
                ?.eventActionComplexPrimaryCta
                ? contentfulContent?.noEventsFoundActionComplex
                    ?.eventActionComplexPrimaryCta
                : "Create an event"}
            </MarmaladeButton>
          </div>
        </div>
      </Container>
    )
  }
  return (
    <Container
      className={classNames(MyEventsStyles.myeventsOuterContainer, {
        [`${MyEventsStyles.myeventsColumnContainer}`]: columnLayout
          ? true
          : false
      })}
    >
      <div className={MyEventsStyles.myeventsTitleWrapper}>
        <Typography
          className={MyEventsStyles.myeventsTitle}
          variant="heading-sm"
          color="neutral-1000"
        >
          {contentfulContent?.noEventsFoundActionComplex
            ?.eventActionComplexHeader
            ? contentfulContent?.noEventsFoundActionComplex
                ?.eventActionComplexHeader
            : "My events"}
        </Typography>
        <EventsBtn
          className={MyEventsStyles.myeventsAllEventsBtnDesktop}
          heapId="button.organizer-component.manage-my-events"
          style={{
            display: columnLayout ? "none" : "inline-block"
          }}
        >
          {contentfulContent?.eventsFoundActionComplex
            ?.eventActionComplexTertiaryCta
            ? contentfulContent?.eventsFoundActionComplex
                ?.eventActionComplexTertiaryCta
            : "View all events"}
        </EventsBtn>
      </div>

      {activeEvent ? (
        <div className={MyEventsStyles.myeventsPrimaryWrapper}>
          <div className={MyEventsStyles.myeventsHeaderImageContainer}>
            <div
              onClick={(e) => handleMobileAppClick(activeEvent.eventPageUrl, e)}
              onTouchStart={(e) => e.preventDefault()}
              onTouchEnd={(e) => {
                e.preventDefault()
                handleMobileAppClick(activeEvent.eventPageUrl, e)
              }}
              data-heap-id="img.organizer-component.image"
              className={MyEventsStyles.aspectRatioBox}
              style={{ cursor: "pointer" }}
            >
              {activeEvent?.imageUrl ? (
                <img
                  className={MyEventsStyles.myeventsHeaderImage}
                  src={activeEvent.imageUrl}
                  alt={`Image for event: ${activeEvent.title}`}
                />
              ) : (
                <img
                  className={MyEventsStyles.myeventsHeaderImage}
                  src={placeholderSvgImg}
                  alt={`Image for event: ${activeEvent.title}`}
                />
              )}
            </div>
          </div>
          <div className={MyEventsStyles.myeventsContentContainer}>
            <Typography
              variant="body-lg"
              className={MyEventsStyles.myeventsEventName}
              color="neutral-1000"
            >
              {activeEvent.title}
            </Typography>
            <Typography variant="body-md-bold">
              {activeEvent.displayDate}
            </Typography>

            <div className={MyEventsStyles.myeventsViewEventBtn}>
              <div
                onClick={(e) =>
                  handleMobileAppClick(activeEvent.eventPageUrl, e)
                }
                onTouchStart={(e) => e.preventDefault()}
                onTouchEnd={(e) => {
                  e.preventDefault()
                  handleMobileAppClick(activeEvent.eventPageUrl, e)
                }}
                data-heap-id="button.organizer-component.manage-event"
                style={{ cursor: "pointer" }}
              >
                <MarmaladeButton variant="primary">
                  {contentfulContent?.eventsFoundActionComplex
                    ?.eventActionComplexPrimaryCta
                    ? contentfulContent?.eventsFoundActionComplex
                        ?.eventActionComplexPrimaryCta
                    : "Manage Event"}
                </MarmaladeButton>
              </div>
            </div>
            <div
              className={MyEventsStyles.myeventsAllEventsBtnMobile}
              style={{
                display: columnLayout ? "inline-block" : ""
              }}
            >
              <EventsBtn heapId="button.organizer-component.manage-my-events">
                {contentfulContent?.eventsFoundActionComplex
                  ?.eventActionComplexTertiaryCta
                  ? contentfulContent?.eventsFoundActionComplex
                      ?.eventActionComplexTertiaryCta
                  : "Manage my events"}
              </EventsBtn>
            </div>
          </div>
        </div>
      ) : null}
    </Container>
  )
}

const EventsBtn: FC<{
  children: ReactNode
  heapId?: string
  className?: string
  style?: React.CSSProperties
}> = ({ className, children, heapId, style }) => {
  const myEventsUrl = `${
    getBaseUrl() ? getBaseUrl() : process.env.GATSBY_PRODUCT_SITE_URL
  }/organizations/events`

  return (
    <div
      onClick={(e) => handleMobileAppClick(myEventsUrl, e)}
      onTouchStart={(e) => e.preventDefault()}
      onTouchEnd={(e) => {
        e.preventDefault()
        handleMobileAppClick(myEventsUrl, e)
      }}
      style={{ ...style, cursor: "pointer" }}
      data-heap-id={heapId ? heapId : ""}
    >
      <MarmaladeButton variant="ghost" className={classNames(className)}>
        {children}
      </MarmaladeButton>
    </div>
  )
}
